import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { BASE_URL, environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private router: Router,
        private tostr: ToastrService,
        private cookie: CookieService
    ) {
        if (this.cookie.get('marketing_token')?.length > 0) {
            this.accessToken = this.cookie.get('marketing_token')
            // this.cookieService.delete('marketing_token', '/',)
            let DOMAIN = environment.production ? 'printingwarehouses.com' : 'printingw.xyz'
            this.cookie.set('marketing_token', '', { domain: DOMAIN, sameSite: 'None', expires: new Date(2020, 1, 1), path: '', secure: true })
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(`${BASE_URL}user/reset/password/`, email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        credentials['device'] = 'web'
        credentials['login_type'] = 'password'
        credentials['access'] = 'marketing'
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(`${BASE_URL}user/login/`, credentials).pipe(
            switchMap((response: any) => {
                if (response.need_otp_verify != true) {
                    // Store the access token in the local storage
                    this.accessToken = response.token;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response;
                }
                // Return a new observable with the response
                return of(response);
            })
        );
    }


    /**
     * Resend OTP
     *
     * @param otp_identifire
     */
    resendOTP(otpIdentifier): Observable<any> {
        return this._httpClient.post(`${BASE_URL}user/resend/otp/`, { otp_identifier: otpIdentifier })
    }

    /**
     * Submit OTP
     *
     * @param otpIdentifier
     * @param otp
     */
    submitOTP(otpIdentifier, otp): Observable<any> {
        return this._httpClient.post(`${BASE_URL}user/verify/otp/`, { otp_identifier: otpIdentifier, otp: otp }).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.token;
                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response;
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(token): Observable<any> {
        // Sign in using the token
        return this._httpClient.post(`${BASE_URL}user/login/token/`, {
            token: token
        }).pipe(
            catchError(() =>
            // Return false
            {
                this.router.navigate(['sign-in'])
                return of(false)
            }
            ),
            switchMap((response: any) => {
                this.accessToken = response.token;
                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response;
                // Return true
                return of(response.token != null);
            })
        );
    }

    checkUserAccess(user) {
        if (user.account_type == 'pwa_designer') {
            this.tostr.error('You are not athorized to use this platform', 'Sorry')
            this.signOut()
            this.router.navigate(['/sign-in'])
            return
        }
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.clear()
        // Set the authenticated flag to false
        this._authenticated = false;
        // Return the observable
        return of(true);
    }


    /**
     * Check the authentication status
     */
    check(token: string = null): Observable<boolean> {

        if (token != null) {
            return this.signInUsingToken(token);
        }

        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability   
        if (!this.accessToken) {
            return of(false);
        }

        // // Check the access token expire date
        // if ( AuthUtils.isTokenExpired(this.accessToken) )
        // {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it

        return of(true);
    }
}
